/**
 * Sends the report and, only when the checkbox is checked, blocks and clears
 * the pending request too. The block leg is skipped on the viewer's own
 * message, because the server refuses a self block (cannotBlockSelf).
 *
 * Order: report first, then block. The report is the store-compliance action
 * and must not be lost to a block failure, so a failed report never attempts
 * the block at all.
 *
 * No confirm dialog lives here. Checking the box and pressing "Send report"
 * is the one confirming action for the "block them too" convenience; raising
 * a second confirm on top of it would contradict that.
 */
import { useMessageReport } from "@sportsmet/messaging-sdk/private";

import { useAuth } from "../../auth/AuthProvider";
import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import type { typeReportReason } from "./moderationCopy";
import {
  isFailedSdkResult,
  isViewerSender,
  sdkErrorCode,
} from "./moderationRules";
import { useBlockAndClearRequest } from "./useBlockFan";

export function useReportMessage(): {
  sendReport: (payload: {
    messageId: string;
    reason: typeReportReason;
    details: string | null;
    blockThemToo: boolean;
    senderAccountId: string;
  }) => Promise<{
    didSend: boolean;
    didBlock: boolean;
    didClearRequest: boolean;
    errorCode: string | null;
  }>;
  isPending: boolean;
} {
  const reportMutation = useMessageReport();
  const { blockAndClear } = useBlockAndClearRequest();
  const { state: authState } = useAuth();
  const viewerUserId =
    authState.status === "signedIn" ? authState.user.userId : "";

  const sendReport = async (payload: {
    messageId: string;
    reason: typeReportReason;
    details: string | null;
    blockThemToo: boolean;
    senderAccountId: string;
  }): Promise<{
    didSend: boolean;
    didBlock: boolean;
    didClearRequest: boolean;
    errorCode: string | null;
  }> => {
    try {
      const reportResult = await reportMutation.mutateAsync({
        messageId: payload.messageId,
        reason: payload.reason,
        details: payload.details,
      });
      if (isFailedSdkResult(reportResult)) {
        showApiError(reportResult);
        return {
          didSend: false,
          didBlock: false,
          didClearRequest: false,
          errorCode: sdkErrorCode(reportResult),
        };
      }

      if (
        !payload.blockThemToo ||
        isViewerSender({ senderId: payload.senderAccountId, viewerUserId })
      ) {
        return {
          didSend: true,
          didBlock: false,
          didClearRequest: false,
          errorCode: null,
        };
      }

      const outcome = await blockAndClear({
        accountId: payload.senderAccountId,
      });
      return {
        didSend: true,
        didBlock: outcome.didBlock,
        didClearRequest: outcome.didClearRequest,
        errorCode: outcome.errorCode,
      };
    } catch {
      showOfflineError();
      return {
        didSend: false,
        didBlock: false,
        didClearRequest: false,
        errorCode: null,
      };
    }
  };

  return { sendReport, isPending: reportMutation.isPending };
}
